import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X, Phone, Ticket, Bell, AlertCircle, CheckCircle, Info } from "lucide-react";

import { cn } from "@/utils/shadcn";

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed top-4 right-4 z-[100] flex max-h-screen w-full flex-col gap-2 md:max-w-[420px]",
      className
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-start space-x-3 overflow-hidden rounded-xl border p-4 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-right-full backdrop-blur-sm",
  {
    variants: {
      variant: {
        default: "border-border bg-background/95 text-foreground",
        destructive:
          "destructive group border-destructive bg-destructive text-destructive-foreground",
        handover:
          "border-primary-200 bg-gradient-to-br from-primary-50/80 to-primary-100/60 text-primary-900 shadow-primary-100/20 dark:border-primary-800/30 dark:from-primary-950/40 dark:to-primary-900/30 dark:text-primary-100 dark:shadow-primary-900/20",
        ticket:
          "border-primary-300 bg-gradient-to-br from-primary-100/90 to-primary-200/70 text-primary-800 shadow-primary-200/30 dark:border-primary-700/40 dark:from-primary-900/50 dark:to-primary-800/40 dark:text-primary-50 dark:shadow-primary-800/30",
        success:
          "border-green-200 bg-gradient-to-br from-green-50/90 to-emerald-100/70 text-green-800 shadow-green-100/20 dark:border-green-800/30 dark:from-green-950/40 dark:to-emerald-950/30 dark:text-green-100 dark:shadow-green-900/20",
        error:
          "border-red-200 bg-gradient-to-br from-red-50/90 to-rose-100/70 text-red-800 shadow-red-100/20 dark:border-red-800/30 dark:from-red-950/40 dark:to-rose-950/30 dark:text-red-100 dark:shadow-red-900/20",
        info: "border-primary-200 bg-gradient-to-br from-primary-50/80 to-calm-blue/60 text-primary-800 shadow-primary-100/20 dark:border-primary-800/30 dark:from-primary-950/40 dark:to-primary-900/30 dark:text-primary-100 dark:shadow-primary-900/20"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => (
  <ToastPrimitives.Root
    ref={ref}
    className={cn(toastVariants({ variant }), className)}
    {...props}
  />
));
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastIcon = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    variant?: "handover" | "ticket" | "success" | "error" | "info" | "default";
  }
>(({ className, variant = "default", ...props }, ref) => {
  const getIcon = () => {
    switch (variant) {
      case "handover":
        return <Phone className="h-5 w-5 animate-pulse" />;
      case "ticket":
        return <Ticket className="h-5 w-5" />;
      case "success":
        return <CheckCircle className="h-5 w-5" />;
      case "error":
        return <AlertCircle className="h-5 w-5" />;
      case "info":
        return <Info className="h-5 w-5" />;
      default:
        return <Bell className="h-5 w-5" />;
    }
  };

  const getIconColors = () => {
    switch (variant) {
      case "handover":
        return "text-primary-700 dark:text-primary-300";
      case "ticket":
        return "text-primary-800 dark:text-primary-200";
      case "success":
        return "text-green-700 dark:text-green-300";
      case "error":
        return "text-red-700 dark:text-red-300";
      case "info":
        return "text-primary-700 dark:text-primary-300";
      default:
        return "text-foreground";
    }
  };

  const getIconBackground = () => {
    switch (variant) {
      case "handover":
        return "bg-primary-100/80 dark:bg-primary-800/40 ring-2 ring-primary-200/50 dark:ring-primary-700/30";
      case "ticket":
        return "bg-primary-200/90 dark:bg-primary-700/50 ring-2 ring-primary-300/60 dark:ring-primary-600/40";
      case "success":
        return "bg-green-100/80 dark:bg-green-800/40 ring-2 ring-green-200/50 dark:ring-green-700/30";
      case "error":
        return "bg-red-100/80 dark:bg-red-800/40 ring-2 ring-red-200/50 dark:ring-red-700/30";
      case "info":
        return "bg-primary-100/80 dark:bg-primary-800/40 ring-2 ring-primary-200/50 dark:ring-primary-700/30";
      default:
        return "bg-muted";
    }
  };

  return (
    <div
      ref={ref}
      className={cn(
        "flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-xl shadow-sm",
        getIconColors(),
        getIconBackground(),
        className
      )}
      {...props}
    >
      {getIcon()}
    </div>
  );
});
ToastIcon.displayName = "ToastIcon";

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive",
      className
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600",
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("text-sm font-semibold leading-tight", className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("text-xs opacity-85 leading-relaxed mt-1", className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastIcon,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction
};
